import React, { useState } from 'react';
import Avatar from '../common/Avatar';
import { Case } from '../../types';

interface CasesTableProps {
  cases?: Case[];
  onRowClick?: (caseId: string) => void;
  onEdit?: (caseId: string) => void;
  onDelete?: (caseId: string) => void;
}

const mockCases: Case[] = [
  {
    id: '1',
    name: 'Contract Dispute - Tech Corp Inc.',
    client: {
      id: 'client1',
      username: 'Tech Corp Inc.',
      email: 'contact@techcorp.com',
      initials: 'TC',
    },
    status: {
      id: 'active',
      label: 'Active',
      color: '#063932',
    },
    assignedTo: {
      id: 'user1',
      username: 'john.smith',
      email: 'john@example.com',
      initials: 'JS',
    },
    createdAt: '2024-01-10T10:30:00Z',
    updatedAt: '2024-01-12T14:20:00Z',
  },
  {
    id: '2',
    name: 'Merger Agreement Review',
    client: {
      id: 'client2',
      username: 'Acme Corporation',
      email: 'legal@acme.com',
      initials: 'AC',
    },
    status: {
      id: 'review',
      label: 'In Review',
      color: '#EC8D18',
    },
    assignedTo: {
      id: 'user2',
      username: 'sarah.jones',
      email: 'sarah@example.com',
      initials: 'SJ',
    },
    createdAt: '2024-01-09T14:20:00Z',
    updatedAt: '2024-01-11T09:15:00Z',
  },
  {
    id: '3',
    name: 'Employment Contract Draft',
    client: {
      id: 'client3',
      username: 'StartupXYZ',
      email: 'hr@startupxyz.com',
      initials: 'SX',
    },
    status: {
      id: 'pending',
      label: 'Pending',
      color: '#A2B9B5',
    },
    createdAt: '2024-01-08T09:15:00Z',
  },
];

const CasesTable: React.FC<CasesTableProps> = ({
  cases = mockCases,
  onRowClick,
  onEdit,
  onDelete,
}) => {
  const [sortField, setSortField] = useState<keyof Case | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: keyof Case) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="dashboard-card rounded-2xl overflow-hidden">
      <div className="p-4 pb-0 mb-0 bg-transparent border-b-0 rounded-t-2xl">
        <h6 className="mb-0 font-semibold leading-normal text-lg text-slate-700 dark:text-slate-200">Cases</h6>
      </div>
      <div className="flex-auto p-4">
        <div className="overflow-x-auto">
          <table className="table-auto w-full align-middle mb-0">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90 cursor-pointer" onClick={() => handleSort('name')}>
                  Case Name
                  {sortField === 'name' && (
                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-2`}></i>
                  )}
                </th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Client</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Status</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Assigned To</th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90 cursor-pointer" onClick={() => handleSort('createdAt')}>
                  Created Date
                  {sortField === 'createdAt' && (
                    <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'} ml-2`}></i>
                  )}
                </th>
                <th className="px-4 py-3 text-left align-middle text-[10px] font-bold uppercase tracking-wider text-slate-500 opacity-80 dark:opacity-90">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-600/50">
              {cases.map((caseItem) => (
                <tr
                  key={caseItem.id}
                  className="align-middle even:bg-slate-50 dark:even:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer transition-colors"
                  onClick={() => onRowClick?.(caseItem.id)}
                >
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="text-sm font-bold text-slate-700 dark:text-slate-200">{caseItem.name}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center">
                      <Avatar user={caseItem.client} size="small" className="mr-3" />
                      <div className="flex flex-col justify-center">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{caseItem.client.username}</span>
                        {caseItem.client.email && <span className="text-xs text-slate-500 dark:text-slate-400">{caseItem.client.email}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase rounded-full"
                      style={{
                        backgroundColor: caseItem.status.color + '20',
                        color: caseItem.status.color,
                      }}
                    >
                      {caseItem.status.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    {caseItem.assignedTo ? (
                      <div className="flex items-center">
                        <Avatar user={caseItem.assignedTo} size="small" className="mr-3" />
                        <div className="flex flex-col justify-center">
                          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{caseItem.assignedTo.username}</span>
                          {caseItem.assignedTo.email && <span className="text-xs text-slate-500 dark:text-slate-400">{caseItem.assignedTo.email}</span>}
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-500 dark:text-slate-400">Unassigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle text-center">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{formatDate(caseItem.createdAt)}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap align-middle">
                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => onEdit?.(caseItem.id)} className="text-slate-500 font-bold text-xs hover:text-slate-700 dark:hover:text-slate-200" title="Edit user">Edit</button>
                      <button onClick={() => onDelete?.(caseItem.id)} className="text-slate-500 font-bold text-xs hover:text-danger" title="Delete">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CasesTable;
